import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import useToggledState from '@zenbusiness/application-commons-hooks/useToggledState';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Text from '@zenbusiness/zui/Text';

import { RELEASE_VELO_ORDER_STATUS } from '#client/constants/experimentsFlags';
import useBankingApplicationStatus from '#client/hooks/useBankingApplicationStatus';
import useCheckoutComplete from '#client/hooks/useCheckoutComplete';
import { useNextUp } from '#client/hooks/useNextUp';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { usePartnerEntity } from '#client/hooks/usePartnerEntity';

import { INFO_TOOLTIP_MESSAGES, SERVICE_EVENT_PROPERTIES } from './constants';
import * as Styled from './OrderTracker.styles';
import OrderTrackerItem from './OrderTrackerItem';
import OrderTrackerUpsells from './OrderTrackerUpsells';
import VeloCtaRow from './VeloCtaRow';
import { AiSlideIn } from '../AiSlideIn';
import {
  useGetTimelineItems,
  getTimelineItemsWithRedirectLinks,
  getTitleMessageSuffix,
  shouldFetchBankingStatus,
  shouldHideBankingTask
} from './utils';
import { HomeSection } from '../HomeSection';

function OrderTracker() {
  const { formatMessage } = useIntl();
  // businessEntityUuid is still required for shouldShowVeloCta;
  // journey URL resolution happens inside useCheckoutComplete via useParams.
  const { businessEntityUuid, businessEntity: { formationStatus } } = useBusinessEntity();
  const { orderTasksWithStatus } = useOrderTrackerStatus();
  const { getCheckoutCompleteUrl } = useCheckoutComplete();
  const { data: nextUpCards = [] } = useNextUp();
  const { isPartner } = usePartnerEntity();
  const isVeloOrderStatusEnabled = useExperimentFlag(RELEASE_VELO_ORDER_STATUS, false);
  const [selectedTask, setSelectedTask] = useState(null);
  const {
    state: orderTrackerDetailOpen,
    on: orderTrackerDetailOpenHandler,
    off: orderTrackerDetailCloseHandler
  } = useToggledState(false);

  const allowedTimelineItems = useGetTimelineItems(orderTasksWithStatus);

  // Check if we have any pending banking tasks that need application status
  const hasPendingBankingTask = allowedTimelineItems.some(shouldFetchBankingStatus);
  const { applicationStatus: bankingApplicationStatus } = useBankingApplicationStatus(
    hasPendingBankingTask
  );

  // Filter out banking tasks with final statuses
  const visibleTimelineItems = allowedTimelineItems.filter(
    (item) => !shouldHideBankingTask(item, bankingApplicationStatus)
  );
  // Determine if we should show Velo CTA (flag enabled AND businessEntityUuid exists AND not a partner)
  // Partners always use AiSlideIn regardless of feature flag
  const shouldShowVeloCta = isVeloOrderStatusEnabled && businessEntityUuid && !isPartner;

  // Clickstream callback for opening AiSlideIn (called unconditionally per Rules of Hooks)
  const openSlideInEvent = useButtonPressedCallback({
    elementName: 'open_order_tracker_smart_assistant',
    pageSlug: 'order_tracker_smart_assistant'
  });

  const openExternalLinkEvent = useButtonPressedCallback({
    elementName: 'open_order_tracker_external_link',
    pageSlug: 'order_tracker'
  });

  const closeButtonOrderTrackerPressEvent = useButtonPressedCallback({
    elementName: 'close_order_tracker_smart_assistant',
    elementText: selectedTask?.type,
    elementValue: selectedTask?.type,
    pageSlug: 'order_tracker_smart_assistant'
  });

  const handleOrderTrackerDetailOpen = (item) => {
    setSelectedTask(item);
    orderTrackerDetailOpenHandler();
  };

  const getItemClickProps = (item) => {
    // link to journey
    if (item.linkTo) {
      return {
        isClickable: true,
        eventCallback: openExternalLinkEvent,
        onClick: () => { window.location.href = item.linkTo; }
      };
    }

    if (item.hasExternalLink && shouldShowVeloCta) {
      return { isClickable: false };
    }

    if (!shouldShowVeloCta) {
      return {
        isClickable: true,
        eventCallback: openSlideInEvent,
        onClick: () => handleOrderTrackerDetailOpen(item)
      };
    }

    return { isClickable: false };
  };

  const handleOrderTrackerDetailClose = () => {
    setSelectedTask(null);
    orderTrackerDetailCloseHandler();
    closeButtonOrderTrackerPressEvent();
  };

  if (!visibleTimelineItems.length) {
    return null;
  }

  const allowedTimelineItemsObject = visibleTimelineItems.reduce(
    (acc, { status, type, data }) => {
      acc[type] = { status };
      if (data?.estimatedFormationDate) {
        acc[type].estimatedFormationDate = data.estimatedFormationDate;
      }
      return acc;
    },
    {}
  );

  const timelineItems = isVeloOrderStatusEnabled ? getTimelineItemsWithRedirectLinks(
    visibleTimelineItems,
    getCheckoutCompleteUrl,
    nextUpCards,
    { formationStatus }
  ) : visibleTimelineItems;
  const lastItemIndex = timelineItems.length - 1;

  return (
    <HomeSection
      title={(
        <Text color="primary" size="xl" weight="medium">
          <FormattedMessage id="dashboard.orderTracker.title" />
        </Text>
      )}
    >
      <Styled.TrackerCard>
        {shouldShowVeloCta && <VeloCtaRow />}
        {timelineItems.map((item, index) => (
          <Styled.ListItemContainer key={item.type}>
            <OrderTrackerItem
              color={item.color}
              ctaMessageId={`dashboard.orderTracker.orderTaskStatus.${item.status}`}
              titleMessageId={`dashboard.orderTracker.orderTaskType.${item.type}`}
              titleMessageSuffix={getTitleMessageSuffix(item)}
              type={item.type}
              showChevron={false}
              hoverTooltipMessageId={item.hoverTooltipMessageId}
              infoTooltipTitleId={INFO_TOOLTIP_MESSAGES[item.type]?.titleId}
              infoTooltipMessageId={INFO_TOOLTIP_MESSAGES[item.type]?.descriptionId}
              {...getItemClickProps(item)}
            />
            {
                /* if it's the first card show upsells */
                index === 0 && (<OrderTrackerUpsells type={item.type} />)
              }
            {index !== lastItemIndex && (
            <Divider />
            )}
          </Styled.ListItemContainer>
        ))}
      </Styled.TrackerCard>

      {selectedTask && !shouldShowVeloCta && (
        <AiSlideIn
          title={formatMessage({
            id: `dashboard.orderTracker.orderTaskType.${selectedTask.type}`
          })}
          reasonsReplacements={{
            'order status overview': 'Overview',
            'order status what to expect': 'What to Expect'
          }}
          promptSlug="order-task-smart-assist"
          fillData={{
            product: selectedTask.type,
            data: JSON.stringify(allowedTimelineItemsObject)
          }}
          aiSlideInOpen={orderTrackerDetailOpen}
          closeHandler={handleOrderTrackerDetailClose}
          eventingProperties={{
            elementName: 'order_tracker_smart_assistant',
            elementValue: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementValue,
            elementText: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementText,
            aiProjectName: 'Order Tracker Detail'
          }}
          errorMessage={formatMessage({ id: 'dashboard.orderTracker.errorGeneric' })}
          footer={formatMessage({ id: 'dashboard.orderTracker.footer' })}
        />
      )}
    </HomeSection>
  );
}

export default OrderTracker;
